import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Alert,
  ActivityIndicator,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { creditCardInfoService } from '../../../../services/creditCardInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('CreditCardInfoInput');

export const CreditCardInfoInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { creditCardId?: string; isEdit?: boolean }>, string>>();
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const [showAddAnotherDone, setShowAddAnotherDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [savedCreditCardId, setSavedCreditCardId] = useState<string | null>(null);
  const creditCardId = (route.params as any)?.creditCardId as string | undefined;
  const isEdit = !!(route.params as any)?.isEdit;

  const [formData, setFormData] = useState({
    cardIssuer: '', // Bank or credit card company
    cardType: '', // Last 4 digits
    website: '',
    username: '',
    password: '',
    notes: '',
  });

  // Load existing data in edit mode
  useEffect(() => {
    if (isEdit && creditCardId) {
      loadCreditCardData();
    }
  }, [isEdit, creditCardId]);

  const loadCreditCardData = async () => {
    try {
      const allCards = await creditCardInfoService.getCreditCardInfo();
      const creditCard = allCards.find(card => card.id === creditCardId);
      
      if (creditCard) {
        setFormData({
          cardIssuer: creditCard.cardIssuer || '',
          cardType: creditCard.cardType || '',
          website: creditCard.onlineAccountUrl || '',
          username: '',
          password: '',
          notes: creditCard.notes || '',
        });

        // Load vault credentials
        try {
          const vaultData = await vaultService.getPortalCredentials(creditCardId!, 'credit-card');
          if (vaultData.username || vaultData.password) {
            setHasOnlinePortal(true);
            setFormData(prev => ({
              ...prev,
              username: vaultData.username || '',
              password: vaultData.password || '',
            }));
          }
        } catch (error) {
          logger.error('Error loading vault data:', error);
        }
      }
    } catch (error) {
      logger.error('Error loading credit card data:', error);
      Alert.alert('Error', 'Failed to load credit card information');
    }
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.cardIssuer.trim()) {
      Alert.alert('Validation Error', 'Please enter the bank or credit card company name.');
      return;
    }

    if (!formData.cardType.trim()) {
      Alert.alert('Validation Error', 'Please enter the last 4 digits of the card.');
      return;
    }

    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((hasOnlinePortal && (formData.username || formData.password)) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }

      const creditCardData = {
        cardIssuer: formData.cardIssuer.trim(),
        cardType: formData.cardType.trim(),
        onlineAccountUrl: formData.website.trim(),
        notes: formData.notes.trim(),
      };

      let savedCard;
      if (isEdit && creditCardId) {
        savedCard = await creditCardInfoService.updateCreditCardInfo(creditCardId, creditCardData);
      } else {
        savedCard = await creditCardInfoService.createCreditCardInfo(creditCardData);
      }

      // Handle wrapped response format { success, data, message }
      const savedCardData = savedCard?.data || savedCard;

      // Store the saved ID for the Done button
      setSavedCreditCardId(savedCardData.id);

      // Store vault credentials if portal is enabled
      if (hasOnlinePortal && (formData.username || formData.password)) {
        await vaultService.storePortalCredentials(savedCardData.id, 'credit-card', {
          username: formData.username,
          password: formData.password,
        });
      }

      setShowAddAnotherDone(true);
    } catch (error) {
      logger.error('Error saving credit card:', error);
      Alert.alert('Error', 'Failed to save credit card information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddAnother = () => {
    // Reset form and hide buttons
    setFormData({
      cardIssuer: '',
      cardType: '',
      website: '',
      username: '',
      password: '',
      notes: '',
    });
    setHasOnlinePortal(false);
    setShowUsername(false);
    setShowPassword(false);
    setShowAddAnotherDone(false);
  };

  const handleDone = () => {
    setSubmitting(false);
    // Navigate to lock/unlock with the saved ID
    const idToUse = savedCreditCardId || creditCardId;
    logger.debug('[Done Debug] Using ID:', idToUse, 'savedCreditCardId:', savedCreditCardId, 'creditCardId:', creditCardId);
    (navigation as any).navigate('CreditCardInfoLockUnlock', { creditCardId: idToUse });
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>{isEdit ? 'Edit' : 'Add'} Credit Card Information</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView
          style={styles.scrollView}
          contentContainerStyle={styles.scrollViewContent}
          keyboardShouldPersistTaps="handled"
          automaticallyAdjustKeyboardInsets={true}
        >
          
          {/* Bank or Credit Card Company */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Bank or Credit Card Company</Text>
            <TextInput
              style={styles.input}
              value={formData.cardIssuer}
              onChangeText={(text) => setFormData({ ...formData, cardIssuer: text })}
              placeholder="Enter bank or credit card company name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          {/* Last 4 digits */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Last 4 Digits on Card</Text>
            <TextInput
              style={styles.input}
              value={formData.cardType}
              onChangeText={(text) => setFormData({ ...formData, cardType: text.replace(/\D/g, '').slice(0, 4) })}
              placeholder="Enter last 4 digits"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={4}
            />
          </View>

          {/* Portal Toggle */}
          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Does this credit card have an online portal?</Text>
              <View style={styles.toggleWrapper}>
                <TouchableOpacity
                  style={[styles.toggleButton, hasOnlinePortal && styles.toggleButtonActive]}
                  onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
                >
                  <View style={[styles.toggleCircle, hasOnlinePortal && styles.toggleCircleActive]} />
                </TouchableOpacity>
                <Text style={styles.toggleText}>{hasOnlinePortal ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>

          {hasOnlinePortal && (
            <>
              <Text style={styles.helperText}>
                We will securely store your login credentials in our encrypted vault. They will only be shared with your successor after your End of Life event.
              </Text>

              {/* Website */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Website Address</Text>
                <TextInput
                  style={styles.input}
                  value={formData.website}
                  onChangeText={(text) => setFormData({ ...formData, website: text.toLowerCase() })}
                  placeholder="Enter website URL"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="url"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              {/* Username */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter username"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showUsername}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')
                      }
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              {/* Password */}
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="password"
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ? 
                        require('../../../../assets/icons/Eye.png') : 
                        require('../../../../assets/icons/Eye-closed.png')
                      }
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          {/* Notes */}
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Notes</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.notes}
              onChangeText={(text) => setFormData({ ...formData, notes: text })}
              placeholder="Enter any additional notes or special instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          {/* Submit Button or Add Another/Done */}
          {showAddAnotherDone ? (
            <View style={styles.addAnotherDoneContainer}>
              <TouchableOpacity 
                style={[styles.submitButton, styles.addAnotherButton]} 
                onPress={handleAddAnother}
              >
                <Text style={styles.submitButtonText}>Add Another</Text>
              </TouchableOpacity>
              <TouchableOpacity 
                style={[styles.submitButton, styles.doneButton]} 
                onPress={handleDone}
              >
                <Text style={styles.submitButtonText}>Done</Text>
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.buttonContainer}>
              <TouchableOpacity
                style={[styles.submitButton, submitting && { opacity: 0.7 }]}
                onPress={handleSubmit}
                disabled={submitting}
              >
                {submitting ? (
                  <ActivityIndicator color="#FFFFFF" />
                ) : (
                  <Text style={styles.submitButtonText}>{isEdit ? 'Update' : 'Submit'}</Text>
                )}
              </TouchableOpacity>
            </View>
          )}

        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    flex: 1,
  },
  toggleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  toggleButton: {
    width: s(60),
    height: s(32),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(16),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#FB923C',
  },
  toggleCircle: {
    width: s(28),
    height: s(28),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleCircleActive: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    minWidth: s(30),
  },
  helperText: {
    fontSize: ms(14, 0.3),
    color: '#FF9F64',
    marginBottom: ms(20),
    textAlign: 'center',
    fontWeight: '500',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  addAnotherDoneContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
    gap: ms(15),
  },
  addAnotherButton: {
    backgroundColor: '#FF9F64',
    flex: 1,
  },
  doneButton: {
    backgroundColor: '#29BFC0',
    flex: 1,
  },
  bottomPadding: {
    height: s(120),
  },
});